import { useTranslation } from "react-i18next"
import { Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { type AdminHomeResponse, type HomeCategoryKey } from "services/adminApi"
import { remainingCategories, type HomeState } from "@/features/home/homeState"
import { CATEGORY_ICONS, CategoryOptionRow, SELECTABLE_OPTIONS } from "./categoryOptions"

interface LiveOverviewProps {
  data: AdminHomeResponse
  onAddCategory: (category: HomeCategoryKey) => void
  onAddProduct: (category: HomeCategoryKey) => void
  state: HomeState
}

const CATEGORY_TITLE_KEYS: Record<HomeCategoryKey, string> = {
  glasses: "home.onboarding.categories.glasses",
  makeup: "home.onboarding.categories.makeup",
  viewer3d: "home.onboarding.categories.viewer3d",
}

export const LiveOverview = ({ data, onAddCategory, onAddProduct, state }: LiveOverviewProps) => {
  const { t } = useTranslation()
  const countsByKey = new Map(data.categories.map((category) => [category.key, category]))
  const remaining = remainingCategories(state.selectedCategories)
  const remainingTryOn = SELECTABLE_OPTIONS.filter(
    (option) => option.group === "tryon" && remaining.includes(option.key)
  )

  return (
    <>
      {/* Usage stats are deferred by design annotation («статистики не будет в
          первых версиях») — the overview card carries only the banner. */}
      <section
        className="rounded-2xl border border-empty-tile bg-card p-6"
        data-testid="home-live-banner"
      >
        <p className="inline-block rounded-md bg-muted px-2 py-2 text-sm">
          {t("home.live.banner", { count: state.totalLive })}
        </p>
      </section>

      <section className="flex flex-col gap-4" data-testid="home-live-categories">
        <h2 className="text-base font-semibold">{t("home.live.categoriesTitle")}</h2>
        <div className="rounded-2xl border border-empty-tile bg-card px-6 py-2">
          {state.selectedCategories.map((key) => {
            const Icon = CATEGORY_ICONS[key]
            const live = countsByKey.get(key)?.productsLive ?? 0

            return (
              <div
                key={key}
                className="flex items-center gap-2 border-b border-empty-tile/60 py-4 last:border-b-0"
                data-testid={`home-category-row-${key}`}
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted">
                  <Icon className="size-6" strokeWidth={1.75} aria-hidden />
                </span>
                <span className="flex-1 text-lg font-semibold">{t(CATEGORY_TITLE_KEYS[key])}</span>
                <span className="flex items-center gap-1.5 text-sm">
                  <span className="size-2 rounded-full bg-emerald-400" aria-hidden />
                  {t("home.live.liveCount", { count: live })}
                </span>
                <Button
                  className="ml-6"
                  onClick={() => onAddProduct(key)}
                  data-testid={`home-add-more-${key}`}
                >
                  {t("home.live.addMoreProduct")}
                </Button>
              </div>
            )
          })}
        </div>
        {remaining.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="self-start" data-testid="home-add-category">
                <Plus data-icon="inline-start" />
                {t("home.live.addCategory")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              {/* A group header with zero remaining rows must not render. */}
              {remainingTryOn.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
                    {t("home.onboarding.groupTryOn")}
                  </DropdownMenuLabel>
                  {remainingTryOn.map((option) => (
                    <DropdownMenuItem
                      key={option.key}
                      onSelect={() => onAddCategory(option.key)}
                      data-testid={`home-add-category-${option.key}`}
                    >
                      <CategoryOptionRow option={option} />
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              {remaining.includes("viewer3d") && (
                <>
                  <DropdownMenuLabel className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
                    {t("home.onboarding.group3d")}
                  </DropdownMenuLabel>
                  {SELECTABLE_OPTIONS.filter((option) => option.group === "3d").map((option) => (
                    <DropdownMenuItem
                      key={option.key}
                      onSelect={() => onAddCategory(option.key)}
                      data-testid={`home-add-category-${option.key}`}
                    >
                      <CategoryOptionRow option={option} />
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </section>
    </>
  )
}
